/**
 * Create / edit dialog for an alert rule.
 *
 * Sections:
 *   1. Name + description
 *   2. Target picker (4 radio modes + conditional field)
 *   3. Condition picker (3 types + per-type threshold field)
 *   4. Output: severity + cooldown + window + channels (read-only for now)
 *
 * Validation is mostly mirrored from the backend — we let the API
 * reject anything we miss and surface the error inline.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { ChannelPicker } from "@/components/channels/ChannelPicker";
import { TagFilter } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  alertsApi,
  IN_APP_CHANNEL_ID,
  type AlertRule,
  type ConditionType,
  type Direction,
  type FailureSource,
  type Severity,
  type TargetKind,
} from "@/lib/api-alerts";
import { workflowsApi } from "@/lib/api-workflows";
import { cn } from "@/lib/cn";


export interface AlertRuleDialogProps {
  open: boolean;
  edit: AlertRule | null;
  onOpenChange: (open: boolean) => void;
}


export function AlertRuleDialog({ open, edit, onOpenChange }: AlertRuleDialogProps) {
  const qc = useQueryClient();
  const isEdit = edit !== null;

  // ── Form state. Reset whenever the dialog opens with a fresh edit. ──
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [targetKind, setTargetKind] = React.useState<TargetKind>("all");
  const [targetWorkflowId, setTargetWorkflowId] = React.useState<string | null>(null);
  const [targetDirection, setTargetDirection] = React.useState<Direction>("inbound");
  const [targetTag, setTargetTag] = React.useState<string[]>([]);
  const [conditionType, setConditionType] = React.useState<ConditionType>("failure_count");
  const [thresholdNum, setThresholdNum] = React.useState<string>("10");
  const [windowMinutes, setWindowMinutes] = React.useState<string>("5");
  const [severity, setSeverity] = React.useState<Severity>("error");
  const [cooldownMinutes, setCooldownMinutes] = React.useState<string>("15");
  const [channelIds, setChannelIds] = React.useState<string[]>([IN_APP_CHANNEL_ID]);
  // Only meaningful when condition_type='failure_count'; default to
  // execution to match the historical (pre-outbox) behaviour.
  const [failureSource, setFailureSource] = React.useState<FailureSource>("execution");

  React.useEffect(() => {
    if (!open) return;
    if (edit) {
      setName(edit.name);
      setDescription(edit.description ?? "");
      setTargetKind(edit.target_kind);
      setTargetWorkflowId(edit.target_workflow_id);
      setTargetDirection(edit.target_direction ?? "inbound");
      setTargetTag(edit.target_tag ? [edit.target_tag] : []);
      setConditionType(edit.condition_type);
      const cfg = edit.condition_config as Record<string, unknown>;
      setThresholdNum(
        String(
          (cfg.threshold ?? cfg.threshold_ms ?? cfg.threshold_seconds ?? 0) as number,
        ),
      );
      setWindowMinutes(String(edit.window_minutes));
      setSeverity(edit.severity);
      setCooldownMinutes(String(edit.cooldown_minutes));
      setChannelIds(edit.channels && edit.channels.length > 0 ? edit.channels : [IN_APP_CHANNEL_ID]);
      setFailureSource(edit.failure_source ?? "execution");
    } else {
      setName("");
      setDescription("");
      setTargetKind("all");
      setTargetWorkflowId(null);
      setTargetDirection("inbound");
      setTargetTag([]);
      setConditionType("failure_count");
      setThresholdNum("10");
      setWindowMinutes("5");
      setSeverity("error");
      setCooldownMinutes("15");
      setChannelIds([IN_APP_CHANNEL_ID]);
      setFailureSource("execution");
    }
  }, [open, edit]);

  // ── Workflow picker source (only fetched when "workflow" target is active) ──
  const workflowsQ = useQuery({
    queryKey: ["alerts-dialog", "workflows"],
    queryFn: () => workflowsApi.list({ page_size: 100 }),
    enabled: open && targetKind === "workflow",
    staleTime: 60_000,
  });
  const workflowOptions = React.useMemo(
    () =>
      (workflowsQ.data?.items ?? []).map((w) => ({
        value: w.id,
        label: w.name,
        description: `${w.direction} · ${w.canonical_model}.v${w.canonical_version}`,
      })),
    [workflowsQ.data],
  );

  // ── Build the payload + submit ──
  function buildPayload() {
    const conditionConfig: Record<string, number> = {};
    if (conditionType === "failure_count") {
      conditionConfig.threshold = parseInt(thresholdNum, 10) || 0;
    } else if (conditionType === "latency_p95") {
      conditionConfig.threshold_ms = parseInt(thresholdNum, 10) || 0;
    }
    // absence has no extra config — window IS the threshold.

    return {
      name: name.trim(),
      description: description.trim() || null,
      target_kind: targetKind,
      target_workflow_id: targetKind === "workflow" ? targetWorkflowId : null,
      target_direction: targetKind === "direction" ? targetDirection : null,
      target_tag: targetKind === "tag" ? targetTag[0] ?? null : null,
      condition_type: conditionType,
      condition_config: conditionConfig,
      window_minutes: parseInt(windowMinutes, 10) || 5,
      failure_source: failureSource,
      severity,
      cooldown_minutes: parseInt(cooldownMinutes, 10) || 15,
      channels: channelIds.length > 0 ? channelIds : [IN_APP_CHANNEL_ID],
    };
  }

  const submit = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (isEdit && edit) {
        return alertsApi.update(edit.id, payload);
      }
      return alertsApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      onOpenChange(false);
    },
  });

  // Quick client-side guard so we don't ping the API with obvious garbage.
  const canSubmit =
    name.trim().length > 0 &&
    (targetKind !== "workflow" || targetWorkflowId !== null) &&
    (targetKind !== "tag" || targetTag.length > 0) &&
    !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isEdit ? `Edit alert: ${edit?.name}` : "New alert rule"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {/* Name + description */}
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="alert-name">Name</Label>
              <Input
                id="alert-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. WMS critical failure burst"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="alert-desc">Description (optional)</Label>
              <Input
                id="alert-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="When should the on-call wake up?"
              />
            </div>
          </div>

          {/* Target */}
          <Section title="Target — what to watch">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(["all", "workflow", "direction", "tag"] as const).map((k) => (
                <RadioCard
                  key={k}
                  active={targetKind === k}
                  onClick={() => setTargetKind(k)}
                  label={
                    k === "all" ? "Any workflow" :
                    k === "workflow" ? "Specific workflow" :
                    k === "direction" ? "By direction" :
                    "By tag"
                  }
                />
              ))}
            </div>
            {targetKind === "workflow" && (
              <div className="mt-2 space-y-1.5">
                <Label>Workflow</Label>
                <Combobox
                  options={workflowOptions}
                  value={targetWorkflowId}
                  onChange={setTargetWorkflowId}
                  placeholder={workflowsQ.isLoading ? "loading…" : "Pick a workflow"}
                  loading={workflowsQ.isLoading}
                />
              </div>
            )}
            {targetKind === "direction" && (
              <div className="mt-2 flex gap-2">
                {(["inbound", "internal", "outbound"] as const).map((d) => (
                  <RadioCard
                    key={d}
                    active={targetDirection === d}
                    onClick={() => setTargetDirection(d)}
                    label={d}
                  />
                ))}
              </div>
            )}
            {targetKind === "tag" && (
              <div className="mt-2 space-y-1.5">
                <Label>Tag</Label>
                <TagFilter
                  value={targetTag}
                  onChange={(next) => setTargetTag(next.slice(0, 1))}
                  scope="workflows"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Workflows carrying this tag will be watched together.
                </p>
              </div>
            )}
          </Section>

          {/* Condition */}
          <Section title="Condition — when to fire">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(["failure_count", "latency_p95", "absence"] as const).map((c) => (
                <RadioCard
                  key={c}
                  active={conditionType === c}
                  onClick={() => setConditionType(c)}
                  label={
                    c === "failure_count" ? "Failure count" :
                    c === "latency_p95" ? "P95 latency" :
                    "Absence"
                  }
                  hint={
                    c === "failure_count" ? "> N failures in window" :
                    c === "latency_p95" ? "p95 > N ms" :
                    "No run in window"
                  }
                />
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {conditionType !== "absence" && (
                <div className="space-y-1.5">
                  <Label htmlFor="alert-threshold">
                    {conditionType === "failure_count" ? "Threshold (count)" : "Threshold (ms)"}
                  </Label>
                  <Input
                    id="alert-threshold"
                    type="number"
                    min="0"
                    value={thresholdNum}
                    onChange={(e) => setThresholdNum(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="alert-window">Window (minutes)</Label>
                <Input
                  id="alert-window"
                  type="number"
                  min="1"
                  max="1440"
                  value={windowMinutes}
                  onChange={(e) => setWindowMinutes(e.target.value)}
                />
              </div>
            </div>

            {/* Failure source — only relevant for failure_count. The
                runtime ignores this field on latency/absence rules so
                we just hide it instead of explaining the no-op. */}
            {conditionType === "failure_count" && (
              <div className="mt-3 space-y-1.5">
                <Label>What counts as a failure</Label>
                <div className="flex gap-2">
                  {(["execution", "delivery"] as const).map((fs) => (
                    <RadioCard
                      key={fs}
                      active={failureSource === fs}
                      onClick={() => setFailureSource(fs)}
                      label={fs === "execution" ? "Workflow run failed" : "Delivery dead-lettered"}
                      hint={
                        fs === "execution"
                          ? "Execution status = failed"
                          : "Outbound dead-lettered after retries"
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* Output */}
          <Section title="Output">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Severity</Label>
                <div className="flex gap-1">
                  {(["warning", "error", "critical"] as const).map((s) => (
                    <RadioCard
                      key={s}
                      active={severity === s}
                      onClick={() => setSeverity(s)}
                      label={s}
                      compact
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="alert-cooldown">Cooldown (minutes)</Label>
                <Input
                  id="alert-cooldown"
                  type="number"
                  min="0"
                  max="1440"
                  value={cooldownMinutes}
                  onChange={(e) => setCooldownMinutes(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Channels</Label>
                <ChannelPicker value={channelIds} onChange={setChannelIds} />
                <p className="text-[11px] text-muted-foreground">
                  Empty selection falls back to the in-app bell. Configure
                  channels in <span className="font-mono">/channels</span>.
                </p>
              </div>
            </div>
          </Section>

          {submit.error && (
            <Alert tone="danger" title="Failed">
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {submit.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            {isEdit ? "Save" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Small inline bits
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {children}
    </section>
  );
}

function RadioCard({
  active,
  onClick,
  label,
  hint,
  compact,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint?: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md border text-left transition",
        compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-xs",
        active
          ? "border-accent bg-accent/10 text-foreground"
          : "border-border bg-surface text-muted-foreground hover:border-muted-foreground",
      )}
    >
      <div className="text-sm font-medium text-foreground">{label}</div>
      {hint && !compact && <div className="text-[11px] leading-snug">{hint}</div>}
    </button>
  );
}
